// CalendarWeekBar.tsx
import { CalendarOutlined, DownOutlined, InfoCircleFilled, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Calendar, Collapse } from 'antd';
import DropOutline from 'assets/drop-outline.svg?react';
import Drop from 'assets/drop.svg?react';
import dayjs, { Dayjs } from 'dayjs';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import SymptomTrackingSurveyModal from 'pages/SymptomTrackingSurvey';
import posthog from 'posthog-js';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Button } from 'components/ui';
import { colors, palette } from 'design';

const useStyles = createUseStyles({
  weekBar: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    padding: '8px 0',
  },
  dayContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 32,
  },
  dayLabel: {
    fontSize: 14,
    color: colors.text.disabled,
    marginBottom: 2,
    textTransform: 'capitalize',
  },
  date: {
    fontSize: 18,
    color: colors.text.disabled,
    fontWeight: 400,
  },
  dateToday: {
    color: colors.text.primary,
    fontWeight: 700,
  },
  datePast: {
    color: colors.text.primary,
  },
  dot: {
    marginTop: 4,
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: colors.border.default,
  },
  dotRed: {
    composes: '$dot',
    background: colors.dataViz.veryBad,
  },
  dotGreen: {
    composes: '$dot',
    background: colors.brand.accent,
  },
  dotRedOutlined: {
    composes: '$dot',
    border: `1px solid ${colors.feedback.error.solid}`,
  },
  dotFertileHollow: {
    composes: '$dot',
    border: `2px solid ${palette.teal[500]}`,
    background: 'transparent',
  },
  dotOvulationHollow: {
    composes: '$dot',
    border: `2px solid ${palette.teal[900]}`,
    background: 'transparent',
  },
  dotFertileFilled: {
    composes: '$dot',
    background: palette.teal[500],
  },
  dotOvulationFilled: {
    composes: '$dot',
    background: palette.teal[900],
  },
  calendarIconContainer: {
    all: 'unset',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    color: colors.brand.accentStrong,
    fontSize: 24,
    width: 32,
    cursor: 'pointer',
  },
  calendarCollapse: {
    '& .ant-collapse-content-box': {
      padding: '0',
      marginBottom: 24,
    },
    // `!important`: antd's own rule for this element
    // (`.ant-collapse > .ant-collapse-item > .ant-collapse-header`) has two
    // unwrapped classes too, tying our specificity, and it wins that tie —
    // so the reset here was never actually landing.
    '& .ant-collapse-header': {
      padding: '0 !important',
    },
    '& .ant-collapse-content > .ant-collapse-content-box, & .ant-collapse-body': {
      paddingLeft: '0',
      paddingRight: '0',
    },
  },
  calendar: {
    background: 'none',
    '& .ant-picker-panel': {
      border: 'none',
    },
    '& .ant-picker-date-panel': {
      backgroundColor: colors.surface.page,
    },
    '& .ant-picker-body thead th': {
      fontSize: 11,
      fontWeight: 600,
      color: colors.text.disabled,
      textAlign: 'center',
      textTransform: 'uppercase',
      paddingBottom: 4,
    },
    '& .ant-picker-cell': {
      padding: 1,
      backgroundColor: 'white',
      border: `solid 3px ${colors.surface.page}`,
    },
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textTransform: 'capitalize',
    fontWeight: 'bold',
    fontSize: 17,
    color: colors.text.primary,
  },
  dateCellContainer: {
    aspectRatio: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    fontSize: 18,
  },
  dateNumberOtherMonth: { color: colors.text.disabled },
  dateNumberPast: {},
  dateNumberToday: { fontWeight: 'bold' },
  dateNumberFuture: { color: colors.text.tertiary },
  dayDots: { display: 'flex', flexDirection: 'row' },
  dateCellTopRow: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginBottom: 3,
    paddingRight: 4,
    height: 3,
    fontSize: 10,
    color: colors.text.disabled,
    width: '100%',
    position: 'relative',
    top: 4,
  },
  dateCellBottomRow: { display: 'flex', flexDirection: 'row' },
  dateCellBottomRowWhenFillingPeriod: {
    composes: '$dateCellBottomRow',
    height: 10,
    '& svg': {
      height: 10,
      width: 10,
    },
  },
  calendarModeSelector: {
    marginTop: 12,
    marginBottom: 12,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noPeriodDay: {
    // hollow circle
    height: 10,
    width: 10,
    border: `2px solid ${colors.text.disabled}`,
    borderRadius: '50%',
  },
  noDataDay: {
    height: 6,
    width: 6,
    backgroundColor: 'white',
  },
  filledOutDay: {
    height: 6,
    width: 6,
    backgroundColor: colors.brand.accent,
    shapeOutside: 'circle()',
    clipPath: 'circle()',
  },
  filledOutDayInPeriodFillingMode: {
    composes: '$filledOutDay',
    height: 10,
    width: 10,
  },
  filledOutDayFertileHollow: {
    height: 6,
    width: 6,
    border: `2px solid ${palette.teal[500]}`,
    borderRadius: '50%',
    boxSizing: 'border-box',
  },
  filledOutDayOvulationHollow: {
    height: 6,
    width: 6,
    border: `2px solid ${palette.teal[900]}`,
    borderRadius: '50%',
    boxSizing: 'border-box',
  },
  filledOutDayFertile: {
    height: 6,
    width: 6,
    backgroundColor: palette.teal[500],
    shapeOutside: 'circle()',
    clipPath: 'circle()',
  },
  filledOutDayOvulation: {
    height: 6,
    width: 6,
    backgroundColor: palette.teal[900],
    shapeOutside: 'circle()',
    clipPath: 'circle()',
  },
  bloodDrop: {
    height: 7,
    width: 7,
  },
  specificDayModalDescription: {
    marginBottom: 0,
    color: colors.text.disabled,
  },
  specificDayModalTitle: {
    marginTop: 0,
  },
  specificDayModalBigValue: {
    margin: 0,
    marginTop: -5,
    fontWeight: 'bold',
    fontSize: 19,
    '& svg': {
      height: 13,
    },
    '& svg:first-child': {
      marginLeft: 10,
    },
  },
  specificDayModalValue: {
    margin: 0,
    whiteSpace: 'pre-line',
  },
});

const CalendarWeekBar: React.FC<{
  isCalendarOpen: boolean;
  setIsCalendarOpen: (isOpen: boolean) => void;
  onFillCycles: () => void;
}> = ({ isCalendarOpen, setIsCalendarOpen, onFillCycles }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const { isPeriodTrackingEnabled } = useShouldAskForPeriod();

  const { getSpecificDailyStatus, refresh, getPredictionForDay, getFertilityStatusForDay } = useSymptomTrackingData();
  const today = dayjs();

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [surveyModalDate, setSurveyModalDate] = useState<Dayjs>(dayjs());

  const weekDays = Array.from({ length: 7 }).map((_, i) => {
    return today.add(i - 3, 'day');
  });

  const getFertilityDotClass = (fertilityStatus: { type: 'ovulation' | 'fertile'; hollow: boolean }) => {
    if (fertilityStatus.type === 'ovulation')
      return fertilityStatus.hollow ? styles.dotOvulationHollow : styles.dotOvulationFilled;
    return fertilityStatus.hollow ? styles.dotFertileHollow : styles.dotFertileFilled;
  };

  const renderFertilityCalendarDot = (
    fertilityStatus: { type: 'ovulation' | 'fertile'; hollow: boolean },
    testId?: string,
  ) => {
    let className: string;
    if (fertilityStatus.type === 'ovulation')
      className = fertilityStatus.hollow ? styles.filledOutDayOvulationHollow : styles.filledOutDayOvulation;
    else className = fertilityStatus.hollow ? styles.filledOutDayFertileHollow : styles.filledOutDayFertile;
    return <div className={className} data-testid={testId} />;
  };

  const computeDayStatus = (date: Dayjs) => {
    const prediction = getPredictionForDay(date);
    const isPeriodPredicted = prediction !== null;
    const fertilityStatus = getFertilityStatusForDay(date);

    if (date.isBefore(today, 'day')) {
      const status = getSpecificDailyStatus(date);
      if (status) {
        if (status.menstruation_amount > 0) return styles.dotRed;
        if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
        return styles.dotGreen;
      }
      if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
    } else if (date.isSame(today, 'day')) {
      const status = getSpecificDailyStatus(date);
      if (status) {
        if (status.menstruation_amount > 0) return styles.dotRed;
        if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
        return styles.dotGreen;
      }
      if (isPeriodPredicted) return styles.dotRedOutlined;
      if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
    } else {
      if (isPeriodPredicted) return styles.dotRedOutlined;
      if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
    }

    return styles.dot;
  };

  const previousMonth = () => {
    // note: we add 2 days to exclude any edge-effects with timezones
    setSelectedDate(selectedDate.startOf('month').subtract(1, 'month').add(2, 'days'));
  };
  const nextMonth = () => {
    // note: we add 2 days to exclude any edge-effects with timezones
    setSelectedDate(selectedDate.startOf('month').add(1, 'month').add(2, 'days'));
  };

  function renderMenstruationLevel(value: number) {
    return [...Array(value)].map((_, i) => <Drop key={i} fill={colors.dataViz.veryBad} className={styles.bloodDrop} />);
  }

  function getDateStyle(date: Dayjs) {
    if (date.month() !== today.month()) return styles.dateNumberOtherMonth;

    if (date.date() < today.date()) return styles.dateNumberPast;
    if (date.date() > today.date()) return styles.dateNumberFuture;

    return styles.dateNumberToday;
  }

  function renderPeriodPredictionIndicator(current: Dayjs) {
    const prediction = getPredictionForDay(current);

    if (!prediction) {
      const fertilityStatus = getFertilityStatusForDay(current);
      if (fertilityStatus)
        return renderFertilityCalendarDot(fertilityStatus, `tracking.fertility-dot-${current.format('YYYY-MM-DD')}`);
      return <div className={styles.noDataDay} />;
    }

    if (prediction.isInMostLikelyPeriod) {
      return <DropOutline height={7} width={7} />;
    }

    return <DropOutline height={7} width={7} style={{ opacity: 0.3 }} />;
  }

  const renderDateCell = (current: Dayjs) => {
    const dayData = getSpecificDailyStatus(current);
    const fertilityStatus = getFertilityStatusForDay(current);
    return (
      <div className={styles.dateCellContainer}>
        <div className={styles.dateCellTopRow}>{dayData && dayData.comment && <InfoCircleFilled />}</div>
        <div className={getDateStyle(current)}>{current.date()}</div>
        {dayData ? (
          <div className={styles.dayDots}>
            <div className={styles.dateCellBottomRow}>
              {dayData.menstruation_amount === 0 ? (
                fertilityStatus ? (
                  renderFertilityCalendarDot(fertilityStatus, `tracking.filled-out-day-${current.format('YYYY-MM-DD')}`)
                ) : (
                  <div
                    className={styles.filledOutDay}
                    data-testid={`tracking.filled-out-day-${current.format('YYYY-MM-DD')}`}
                  />
                )
              ) : (
                renderMenstruationLevel(dayData.menstruation_amount)
              )}
            </div>
          </div>
        ) : (
          renderPeriodPredictionIndicator(current)
        )}
      </div>
    );
  };

  const renderCalendarHeader = ({ value }: { value: Dayjs }) => {
    return (
      <div className={styles.calendarHeader}>
        <Button onClick={previousMonth}>
          <LeftOutlined />
        </Button>
        {value.format('MMMM YYYY')}
        <Button onClick={nextMonth}>
          <RightOutlined />
        </Button>
      </div>
    );
  };

  const onDateSelected = (date: Dayjs) => {
    setSelectedDate(date);
    if (date <= today) {
      setSurveyModalDate(date);
      setIsSurveyModalOpen(true);
    }
    posthog.capture('tracking.calendar_day_selected');
  };

  const toggleCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  return (
    <div style={{ width: '100%' }}>
      <Collapse
        activeKey={isCalendarOpen ? ['calendar'] : []}
        ghost
        className={styles.calendarCollapse}
        items={[
          {
            key: 'calendar',
            showArrow: false,
            children: (
              <>
                <Calendar
                  className={styles.calendar}
                  fullscreen={false}
                  fullCellRender={renderDateCell}
                  headerRender={renderCalendarHeader}
                  onSelect={onDateSelected}
                  value={selectedDate}
                />
                <div className={styles.calendarModeSelector}>
                  {isPeriodTrackingEnabled && (
                    <>
                      <Button
                        icon={<Drop height={16} width={16} fill={colors.dataViz.veryBad} />}
                        size="lg"
                        onClick={onFillCycles}
                      >
                        {t('symptomTracking.cycles.fillCycles')}
                      </Button>
                    </>
                  )}
                </div>
              </>
            ),
          },
        ]}
      />
      <div className={styles.weekBar}>
        {weekDays.map((day, idx) => (
          <div
            className={styles.dayContainer}
            key={idx}
            onClick={() => {
              if (day.date() <= today.date()) {
                setSurveyModalDate(day);
                setIsSurveyModalOpen(true);
              }
            }}
          >
            <span className={styles.dayLabel}>{day.format('dd').slice(0, 2)}</span>
            <span
              className={
                day.date() < today.date()
                  ? `${styles.date} ${styles.datePast}`
                  : day.date() === today.date()
                    ? `${styles.date} ${styles.dateToday}`
                    : styles.date
              }
            >
              {day.date()}
            </span>
            <span className={computeDayStatus(day)} />
          </div>
        ))}
        <button
          className={styles.calendarIconContainer}
          onClick={toggleCalendar}
          aria-expanded={isCalendarOpen}
          aria-label="Toggle calendar view"
          type="button"
        >
          <CalendarOutlined />
          <DownOutlined style={{ fontSize: 15, transform: isCalendarOpen ? 'rotate(180deg)' : 'none' }} />
        </button>
      </div>
      <SymptomTrackingSurveyModal
        open={isSurveyModalOpen}
        key={surveyModalDate.format('YYYY-MM-DD')}
        onClose={() => {
          setIsSurveyModalOpen(false);
          refresh();
        }}
        date={surveyModalDate}
      />
    </div>
  );
};

export default CalendarWeekBar;
